import React from 'react';

/**
 * Props for a {@link ProgressSpinner} component.
 */
export type ProgressSpinnerProps = {
  percentage: number;
  dimension?: number;
  showPercentageText?: boolean;
  ariaLabel?: string;
  ariaHidden?: boolean;
};

/**
 * A circular progress spinner component that visually represents a percentage of completion.
 * @param percentage the current percentage to display (0-100)
 * @param dimension the dimension (width and height) of the spinner in pixels (default is 45)
 * @param showPercentageText whether to show the percentage text inside the spinner. By default, the percentage text is hidden.
 * @param ariaLabel optional aria-label for accessibility to better describe the spinner control, in addition aria-valuetext.
 * @param ariaHidden optional aria-hidden for accessibility to hide the spinner from screen readers. By default, the spinner is visible to screen readers.
 * @param props additional props to pass to the root element
 * @constructor
 */
export function ProgressSpinner({
  percentage,
  dimension = 45,
  showPercentageText,
  ariaLabel,
  ariaHidden,
  ...props
}: Readonly<ProgressSpinnerProps>): JSX.Element {
  percentage = Math.max(0, Math.min(percentage, 100));

  const strokeWidth = 8;
  const radius = (dimension - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - percentage / 100);

  return (
    <div
      {...props}
      className={
        'ehs-progress-spinner' +
        (showPercentageText ? ' show-percentage-text' : '')
      }
      style={{
        minWidth: dimension + 'px',
        minHeight: dimension + 'px'
      }}
      data-percent={percentage}
      role="progressbar"
      aria-valuenow={!ariaHidden ? percentage : undefined}
      aria-valuemin={!ariaHidden ? 0 : undefined}
      aria-valuemax={!ariaHidden ? 100 : undefined}
      aria-valuetext={!ariaHidden ? `${percentage}%` : undefined}
      aria-label={!ariaHidden ? ariaLabel : undefined}
      aria-hidden={ariaHidden}
    >
      <svg width={dimension} height={dimension}>
        <circle
          className="ehs-progress-spinner-background-circle"
          cx={dimension / 2}
          cy={dimension / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <circle
          className="ehs-progress-spinner-foreground-circle"
          cx={dimension / 2}
          cy={dimension / 2}
          r={radius}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      {showPercentageText && (
        <span
          className="ehs-progress-spinner-percentage-text"
          style={{
            width: dimension,
            height: dimension
          }}
        >
          {percentage}%
        </span>
      )}
    </div>
  );
}
